import { Injectable, RendererFactory2, inject } from '@angular/core';

import { ReplaySubject, take } from 'rxjs';

import { RecaptchaService } from '@hdms/shared/data-access';
import {
  EnvironmentService,
  FeatureToggleService,
  StorageService,
  StorageType,
  WindowRefService
} from '@hdms/shared/util-core';

export interface ChatCustomAttributes {
  /* eslint-disable @typescript-eslint/naming-convention */
  WebsiteName?: string;
  WebsiteEmailAddress?: string;
  WebsiteCountryCode?: string;
  WebsiteLanguageCode?: string;
  WebsiteCaptchaToken?: string;
  /* eslint-enable @typescript-eslint/naming-convention */
}

export enum ChatStorageKey {
  SESSION_ACTIVE = ':gcmcsessionActive',
  CHAT_OPEN = ':gcmcopn'
}

export enum ProviderParams {
  COMMAND = 'command',
  SUBSCRIBE = 'subscribe',
  DATABASE_SET = 'Database.set',
  LAUNCHER_READY = 'Launcher.ready',
  LAUNCHER_SHOW = 'Launcher.show',
  MESSENGER_READY = 'Messenger.ready',
  MESSENGER_OPEN = 'Messenger.open'
}

interface ChatStorageObj {
  value?: string;
  ttl?: string;
}

interface GenesysWindow extends Window {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Genesys: (command: string, action: string, cb?: (() => void) | { messaging: ChatCustomAttributes }) => void;
}

export const FT_HEADER_CHAT_RECAPTCHA_ENABLED = 'ldt_webchat_captcha';
export const FT_HEADER_CHAT_RESEND_ATTRIBUTES_ENABLED = 'ldt_resend_webchat_attributes';

@Injectable({
  providedIn: 'root'
})
export class LiveChatProviderService {
  private readonly envService = inject(EnvironmentService);
  private readonly webchatDeploymentId = this.envService.liveChatDeploymentId;
  private readonly chatInitialized$ = new ReplaySubject<boolean>(1);
  private readonly chatProviderUrl = this.envService.liveChatJsUrl;

  private readonly win = inject<WindowRefService<GenesysWindow>>(WindowRefService).nativeWindow;
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly storageService = inject(StorageService);
  private readonly recaptchaService = inject(RecaptchaService);
  private readonly isChatRecaptchaFtEnabled = inject(FeatureToggleService).getToggleById(
    FT_HEADER_CHAT_RECAPTCHA_ENABLED
  );
  private readonly isResendAttributesFtEnabled = inject(FeatureToggleService).getToggleById(
    FT_HEADER_CHAT_RESEND_ATTRIBUTES_ENABLED
  );

  public readonly isChatInitialized$ = this.chatInitialized$.asObservable();

  get isChatSessionActive(): boolean {
    return this.checkStorage(ChatStorageKey.SESSION_ACTIVE, true) || this.checkStorage(ChatStorageKey.CHAT_OPEN, false);
  }

  get isChatWindowOpen(): boolean {
    return this.checkStorage(ChatStorageKey.CHAT_OPEN, true);
  }

  get isChatJsAvailable(): boolean {
    return typeof this.win.Genesys === 'function';
  }

  public checkActiveSession(): void {
    if (this.isChatSessionActive) {
      this.injectLiveChatJS();
      this.resendChatAttributes();
    }
  }

  public injectLiveChatJS(): void {
    if (!this.win.Genesys) {
      const renderer = this.rendererFactory.createRenderer(null, null);
      const script = renderer.createElement('script');

      const scriptBody = renderer.createText(`
        (function (g, e, n, es, ys) {
          g['_genesysJs'] = e;
          g[e] = g[e] || function () {
            (g[e].q = g[e].q || []).push(arguments)
          };
          g[e].t = 1 * new Date();
          g[e].c = es;
          ys = document.createElement('script'); ys.async = 1; ys.src = n; ys.charset = 'utf-8'; document.head.appendChild(ys);
        })(window, 'Genesys', '${this.chatProviderUrl}', {
          environment: 'euc1',
          deploymentId: '${this.webchatDeploymentId}'
        });
      `);

      renderer.appendChild(script, scriptBody);
      renderer.appendChild(document.head, script);
    }
    this.checkLauncherStatus();
  }

  public checkLauncherStatus(): void {
    if (this.isChatJsAvailable) {
      this.win.Genesys(ProviderParams.SUBSCRIBE, ProviderParams.LAUNCHER_READY, () => {
        this.chatInitialized$.next(true);
        this.chatInitialized$.complete();
        if (this.isChatSessionActive) {
          this.win.Genesys(ProviderParams.COMMAND, ProviderParams.LAUNCHER_SHOW);
        }
      });
    }
  }

  public openMessenger(customAttributes?: ChatCustomAttributes): void {
    if (this.isChatJsAvailable) {
      if (customAttributes) {
        this.setDatabaseAttributes(customAttributes);
      }

      this.win.Genesys(ProviderParams.COMMAND, ProviderParams.MESSENGER_OPEN);
    }
  }

  private setDatabaseAttributes(customAttributes: ChatCustomAttributes): void {
    if (this.isChatJsAvailable) {
      this.win.Genesys(ProviderParams.COMMAND, ProviderParams.DATABASE_SET, {
        messaging: { customAttributes } as ChatCustomAttributes
      });
    }
  }

  /**
   * @param flag - ChatStorageKey
   * @param returnItemValue - true if you want the value in the item. Otherwise, it'll return if the item actually exists.
   * @returns boolean
   */
  private checkStorage(flag: string, returnItemValue: boolean): boolean {
    const key = `_${this.webchatDeploymentId}${flag}`;
    const item = this.storageService.getItem<ChatStorageObj>(key, StorageType.LOCAL, true);
    return returnItemValue ? item?.value === 'true' || false : item !== null;
  }

  /**
   * If the user returns and already has a previous chat in progress,
   * we need to re-send the database attributes to Genesys
   */
  private resendChatAttributes(): void {
    if (this.isResendAttributesFtEnabled) {
      const liveChatRecaptchaKey = this.envService.liveChatRecaptchaV3PublicKey;
      const chatAttributes: ChatCustomAttributes = {
        /* eslint-disable @typescript-eslint/naming-convention */
        WebsiteCountryCode: this.envService.country,
        WebsiteLanguageCode: this.envService.language.toUpperCase()
        /* eslint-enable @typescript-eslint/naming-convention */
      };

      if (liveChatRecaptchaKey && this.isChatRecaptchaFtEnabled) {
        this.recaptchaService
          .generateV3Token$({ siteKey: liveChatRecaptchaKey })
          .pipe(take(1))
          .subscribe({
            next: token => {
              chatAttributes.WebsiteCaptchaToken = token;
              this.setDatabaseAttributes(chatAttributes);
            }
          });
      } else {
        this.setDatabaseAttributes(chatAttributes);
      }
    }
  }
}
